import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {currentProjectStore} from 'common/report/stores/project-details';
import {EntityType, EntityTypePermission} from 'common/report/util/saved-entities/types';
import {userDetailsStore} from 'common/report/stores/user-details';
import {
  allowStaffOverride,
  getEntityTypeName,
  isSharableEntityType,
  isSharingEnabledForProject,
} from 'common/report/util/saved-entities';
import {ActionEvent} from 'common/components/banner/types';
import {reporter} from 'common/report/error-reporting';

import {SharingBannerAttrs} from './types';
import {template} from './template';

const shareBannerKey = `create-entity-share`; // maintain for legacy

export class SharingBanner extends Component<never, SharingBannerAttrs> {
  static get attrsSchema(): AttrsSchema<SharingBannerAttrs> {
    return {
      ...super.attrsSchema,
      'entity-type': {
        type: `string`,
        description: `Type of entity being created`,
        enum: Object.values(EntityType),
      },
      'project-id': {type: `number`},
    };
  }

  get config() {
    return {
      template,
      useShadowDom: true,
      helpers: {
        getEntityTypeName,
        getShareBannerText: () => {
          const userIsConsumer =
            !userDetailsStore.value.projects[this.attr(`project-id`)]?.permissions.includes(
              EntityTypePermission.Visibility,
            ) && !allowStaffOverride(this.attr(`project-id`));
          const privateReasonString = userIsConsumer
            ? `due to your project role (Consumer)`
            : `until you share with others`;
          return `This ${getEntityTypeName(this.attr(`entity-type`))} will be private ${privateReasonString}`;
        },
        handleBannerAction: ({detail}: ActionEvent) => {
          if (detail.actionName === `dismiss`) {
            this.persistShareBannerDismiss();
          }
        },
        shouldShow: () =>
          isSharingEnabledForProject(currentProjectStore.value) &&
          isSharableEntityType(this.attr(`entity-type`)) &&
          !this.getShareBannerDismiss().includes(this.attr(`entity-type`)),
      },
    };
  }

  persistShareBannerDismiss() {
    const parsedValue = this.getShareBannerDismiss();
    try {
      window.localStorage.setItem(shareBannerKey, JSON.stringify([...parsedValue, this.attr(`entity-type`)]));
      this.update();
    } catch (err) {
      reporter.warn(err, `Unable to persist ${shareBannerKey} to localStorage. User will continue to see the banner.`);
    }
  }

  getShareBannerDismiss(): EntityType[] {
    try {
      const lsValue = window.localStorage.getItem(shareBannerKey);
      return lsValue ? JSON.parse(lsValue) : [];
    } catch (err) {
      reporter.warn(
        err,
        `Unable to retrieve ${shareBannerKey} value from localStorage. User will continue to see the banner.`,
      );
      return [];
    }
  }

  updateCallback = () => {
    this.update();
  };

  connectedCallback(): void {
    super.connectedCallback();
    userDetailsStore.subscribe(this.updateCallback).fetch();
    currentProjectStore.subscribe(this.updateCallback).fetch();
  }
  disconnectedCallback(): void {
    userDetailsStore.unsubscribe(this.updateCallback);
    currentProjectStore.unsubscribe(this.updateCallback);
    super.disconnectedCallback();
  }
}

export const SharingBannerEl = defineAndMakeJsxFactory(`mp-sharing-banner`, SharingBanner);
